import * as Sentry from '@sentry/react';

/**
 * Sentryにエラーを送信する
 * @param error エラーオブジェクト
 * @param context 追加コンテキスト情報
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

/**
 * Sentryにメッセージを送信する
 * @param message メッセージ
 * @param level ログレベル
 * @param context 追加コンテキスト情報
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>,
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureMessage(message);
  });
};

/**
 * ユーザー情報をSentryに設定する
 * @param userId ユーザーID
 * @param userInfo ユーザー情報
 */
export const setUser = (userId: string, userInfo?: Record<string, any>) => {
  Sentry.setUser({
    id: userId,
    ...userInfo,
  });
};

/**
 * タグを設定する（フィルタリングに使用）
 * @param tags タグオブジェクト
 */
export const setTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
};

/**
 * アプリケーションのパフォーマンス計測用ユーティリティ
 * （ブラウザのPerformance APIを利用）
 */
export const performance = {
  /**
   * パフォーマンス計測を開始する
   * @param markName マーク名
   */
  mark: (markName: string): void => {
    try {
      if (window.performance && window.performance.mark) {
        window.performance.mark(markName);
      }
    } catch (error) {}
  },

  /**
   * パフォーマンス計測を終了し、計測結果を取得する
   * @param measureName 計測名
   * @param startMarkName 開始マーク名
   * @param endMarkName 終了マーク名（指定しない場合は現在時刻）
   */
  measure: (measureName: string, startMarkName: string, endMarkName?: string): number | null => {
    try {
      if (window.performance && window.performance.measure) {
        // 終了マークを記録（指定がなければ現在時刻）
        if (endMarkName) {
          window.performance.mark(endMarkName);
        } else {
          endMarkName = `${startMarkName}_end`;
          window.performance.mark(endMarkName);
        }

        // 計測を実行
        window.performance.measure(measureName, startMarkName, endMarkName);

        // 計測結果を取得
        const entries = window.performance.getEntriesByName(measureName, 'measure');
        if (entries.length > 0) {
          // 計測結果をSentryに送信
          Sentry.setExtra(`performance_${measureName}`, entries[0].duration);
          return entries[0].duration;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * 特定の操作のパフォーマンスを計測する
   * @param operationName 操作名
   * @param callback 計測対象の関数
   * @returns 関数の戻り値
   */
  trackOperation: async <T>(operationName: string, callback: () => Promise<T>): Promise<T> => {
    const markName = `op_${operationName}_start`;
    try {
      performance.mark(markName);
      const result = await callback();
      performance.measure(`Operation: ${operationName}`, markName);
      return result;
    } catch (error) {
      performance.measure(`Operation: ${operationName} (failed)`, markName);
      throw error;
    }
  },
};

/**
 * try-catchブロックでのエラーハンドリングヘルパー
 * @param fn 実行する非同期関数
 * @param errorContext エラー時のコンテキスト
 * @returns 関数の戻り値
 */
export const tryCatch = async <T>(
  fn: () => Promise<T>,
  errorContext?: Record<string, any>,
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    captureException(error instanceof Error ? error : new Error(String(error)), errorContext);
    throw error;
  }
};
